import React from 'react';

// Coordinator definitions matching the architecture diagram
const COORDINATORS = [
  {
    id: 'DiscoveryCoordinator',
    icon: '🔍',
    label: 'Discovery Coordinator',
    layer: 'Layer 1',
    subAgents: [
      { id: 'git-scan',    icon: '📁', label: 'GitScan',    desc: 'Scans Git repo metadata' },
      { id: 'servicenow',  icon: '🎫', label: 'ServiceNow', desc: 'Queries CMDB for app metadata' },
      { id: 'cmdb-scan',   icon: '🗄️', label: 'CMDB Scan',  desc: 'Infrastructure dependencies' },
    ],
  },
  {
    id: 'AssessmentCoordinator',
    icon: '🧠',
    label: 'Assessment Coordinator',
    layer: 'Layer 1',
    subAgents: [
      { id: 'arch-assess',     icon: '🏛️', label: 'Arch-Assess',     desc: 'Architecture quality (coupling, cohesion, testability)' },
      { id: 'security-assess', icon: '🔒', label: 'Security-Assess',  desc: 'OWASP risk assessment' },
      { id: 'cost-assess',     icon: '💰', label: 'Cost-Assess',      desc: 'Migration cost estimation' },
    ],
  },
  {
    id: 'RecommendationCoordinator',
    icon: '🎯',
    label: 'Recommendation Coordinator',
    layer: 'Layer 1',
    subAgents: [
      { id: '6r-recommend', icon: '6️⃣', label: '6R-Recommend', desc: 'Retire/Retain/Rehost/Replatform/Refactor/Rebuild' },
    ],
  },
  {
    id: 'GenerationCoordinator',
    icon: '⚙️',
    label: 'Generation Coordinator',
    layer: 'Layer 1',
    subAgents: [
      { id: 'spec-gen', icon: '📝', label: 'Spec-Gen', desc: 'Requirements enriched with Wiki context' },
      { id: 'code-gen', icon: '💻', label: 'Code-Gen', desc: 'Code generation with Wiki context' },
    ],
  },
];

function StatusDot({ status }) {
  const cfg = {
    running:   { color: '#0969DA', bg: '#DDF4FF', label: 'Running',   anim: true },
    completed: { color: '#1A7F37', bg: '#DAFBE1', label: 'Done',      anim: false },
    failed:    { color: '#CF222E', bg: '#FFEBE9', label: 'Failed',    anim: false },
    idle:      { color: '#8B949E', bg: '#F6F8FA', label: 'Idle',      anim: false },
  };
  const c = cfg[status] || cfg.idle;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
      background: c.bg, color: c.color,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: c.color,
        animation: c.anim ? 'pulse-dot 1.2s ease infinite' : 'none',
        display: 'inline-block',
      }} />
      {c.label}
    </span>
  );
}

function SubAgentRow({ sub, events }) {
  // Find the latest event for this sub-agent
  const latest = [...events].reverse().find(e =>
    e.type === 'sub_agent_done' && e.data?.sub_agent?.includes(sub.id)
  );
  const running = events.some(e =>
    e.type === 'coordinator_start' && !events.some(f => f.type === 'coordinator_done')
  );

  const status = latest ? (latest.data.success ? 'completed' : 'failed')
               : running ? 'running' : 'idle';
  const durationMs = latest?.data?.duration_ms;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 10px 5px 24px',
      borderLeft: '2px solid #D0D7DE', marginLeft: 16,
      fontSize: 11,
    }}>
      <span>{sub.icon}</span>
      <span style={{ fontWeight: 600, color: '#1A1F2E', minWidth: 110 }}>{sub.label}</span>
      <span style={{ color: '#8B949E', flex: 1, fontSize: 10 }}>{sub.desc}</span>
      <StatusDot status={status} />
      {durationMs && (
        <span style={{ fontSize: 10, color: '#57606A', minWidth: 40, textAlign: 'right' }}>
          {durationMs < 1000 ? `${durationMs}ms` : `${(durationMs/1000).toFixed(1)}s`}
        </span>
      )}
    </div>
  );
}

function CoordinatorCard({ coord, events }) {
  const startEvt = events.find(e => e.type === 'coordinator_start' && e.data?.coordinator === coord.id);
  const doneEvt  = events.find(e => e.type === 'coordinator_done'  && e.data?.coordinator === coord.id);

  const status = doneEvt ? 'completed' : startEvt ? 'running' : 'idle';
  const durationMs = doneEvt?.data?.duration_ms;

  return (
    <div style={{
      border: `1px solid ${status === 'running' ? '#0969DA' : status === 'completed' ? '#1A7F37' : '#D0D7DE'}`,
      borderRadius: 8, overflow: 'hidden',
      background: status === 'running' ? '#F0F8FF' : '#fff',
      transition: 'all 0.2s',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px',
        background: status === 'completed' ? '#DAFBE1' : status === 'running' ? '#DDF4FF' : '#F6F8FA',
        borderBottom: '1px solid #D0D7DE',
      }}>
        <span style={{ fontSize: 16 }}>{coord.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#1A1F2E' }}>{coord.label}</div>
          <div style={{ fontSize: 9, color: '#8B949E' }}>{coord.layer} — Stage Coordination</div>
        </div>
        <StatusDot status={status} />
        {durationMs && (
          <span style={{ fontSize: 10, color: '#57606A' }}>
            {durationMs < 60000 ? `${(durationMs/1000).toFixed(1)}s` : `${Math.floor(durationMs/60000)}m`}
          </span>
        )}
      </div>

      {/* Sub-agents */}
      <div style={{ padding: '6px 0' }}>
        {coord.subAgents.map(sub => (
          <SubAgentRow key={sub.id} sub={sub} events={events} />
        ))}
      </div>
    </div>
  );
}

export default function CoordinatorPanel({ sseEvents }) {
  const events = sseEvents || [];

  const anyActive = COORDINATORS.some(c =>
    events.some(e => e.type === 'coordinator_start' && e.data?.coordinator === c.id)
  );

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title-accent">🎯</span> Stage Coordinators
        <span style={{ marginLeft: 8, fontSize: 10, color: '#57606A' }}>Layer 1 — Agent Mesh</span>
        {anyActive && (
          <span style={{ marginLeft: 'auto', fontSize: 10, background: '#DDF4FF',
            color: '#0969DA', padding: '1px 8px', borderRadius: 8, fontWeight: 700 }}>
            Active
          </span>
        )}
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>

      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {COORDINATORS.map(coord => (
          <CoordinatorCard key={coord.id} coord={coord} events={events} />
        ))}
        {!anyActive && (
          <div style={{ textAlign: 'center', color: '#8B949E', fontSize: 11,
            fontStyle: 'italic', padding: '8px 0' }}>
            Coordinators activate when the pipeline runs
          </div>
        )}
      </div>
    </div>
  );
}
